'use client';
import { cn } from '@/lib/utils';

import Badge from '@/components/atoms/badge';
import LinkButton from '@/components/atoms/LinkButton';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { gradientDecodoBorderStyling } from '@/styles/gradient';
import iconRocketSrc from '~/svg/IconRocket.svg';
import iconShoppingCartSrc from '~/svg/IconShoppingCart.svg';
import { SvgIcon } from '@/components/SvgIcon';

export type PricingRowProps = {
  title: string;
  discount?: string;
  discountLabel?: string;
  price: number;
  pricePer: 'IP' | 'GB';
  priceTotal?: number;
  priceTotalOld?: number;
  priceTotalPostfixText?: string;
  buttonText: string;
  buttonVariant?: 'filled' | 'outlined';
  url: string;
  outlineColor?: 'grey' | 'black';
  outlineText?: string;
  icon: React.ReactNode;
  showMoneyBack?: boolean;
  moneyBack?: string;
};

export default function PricingRow({
  title,
  discount,
  discountLabel,
  price,
  pricePer,
  priceTotal,
  priceTotalPostfixText,
  buttonText,
  buttonVariant,
  url,
  outlineText,
  showMoneyBack,
  moneyBack,
}: PricingRowProps) {
  const { texts } = useTranslation();
  const planId = url?.split('planId=')[1] || '';
  const trackId = `${title}-${planId}`;

  const formattedPricePer = pricePer.replaceAll('-', ' ').replaceAll('ww', '');

  const transformNumberToWithTwoDecimals = (number: number) => {
    const numberString = number.toString();
    if (numberString === '0') {
      return '0.00';
    }
    return number.toFixed(2);
  };

  const transformPriceTotal = (priceTotal: number) => {
    const formatter = new Intl.NumberFormat('en-US');
    return formatter.format(priceTotal);
  };

  return (
    <div className='relative mb-2 md:flex'>
      {outlineText && (
        <div className='text-body-s-medium bg-gradient-main text-base-white md:writing-mode-sideways-lr dark:text-base-dark-white relative z-10 -mb-1 flex h-full items-center justify-center whitespace-nowrap rounded-t font-serif md:absolute md:bottom-0 md:right-full md:top-0 md:-mr-1 md:mb-0 md:origin-bottom-left md:rounded-l md:rounded-tr-none'>
          <span className='md:safari-text inline-block'>{outlineText}</span>
        </div>
      )}
      <div
        data-tracker-component='card'
        className={cn(
          `shadow-card bg-base-white dark:bg-gray-dark-900 relative w-full items-center justify-between border border-solid px-6 py-6 md:flex md:py-4 dark:shadow-none ${
            outlineText
              ? `rounded-r border-l-0 ${gradientDecodoBorderStyling({
                  borderWidth: 'p-px',
                  color: 'gradient-main',
                  borderRadius: 'rounded',
                })}`
              : 'rounded'
          }
          `
        )}
      >
        <div className='flex basis-1/4 items-center justify-center pb-6 md:justify-start md:pb-0'>
          <div className='flex h-7 w-full items-center justify-center rounded bg-gray-50 py-1 text-center md:w-fit md:bg-transparent md:py-0 md:text-left'>
            <p className='text-body-m dark:text-gray-dark-25 font-mono font-normal md:font-serif'>
              {title}
            </p>
          </div>
        </div>

        <div className='flex w-full basis-1/4 items-center justify-between pb-4 md:w-fit md:justify-normal md:pb-0'>
          <p>
            <span className='text-button-text-l dark:text-gray-dark-25 font-serif'>
              ${transformNumberToWithTwoDecimals(price)}
            </span>
            <span className='text-body-s ml-1 font-normal text-gray-500'>
              /{formattedPricePer}
            </span>
          </p>

          <div className={cn(!discount && 'invisible')}>
            <Badge
              color='gradient'
              variant='soft'
              classNames={{ root: 'ml-3 max-h-4 font-serif' }}
            >
              {discount && (
                <>
                  {discountLabel} {discount}
                </>
              )}
            </Badge>
          </div>
        </div>

        {priceTotal && (
          <div className='grid basis-1/4 justify-items-start pb-6 md:pb-0'>
            <span className='text-body-m md:text-button-text-l font-serif font-normal'>
              ${transformPriceTotal(priceTotal)} {texts.pricing_table.total}
            </span>
            <div className='text-body-s min-h-10 flex-col font-serif text-gray-500 md:min-h-min md:flex-row md:text-left lg:flex'>
              <span className='mr-1'>{priceTotalPostfixText}</span>
              {showMoneyBack && (
                <div className='flex'>
                  <p className='mr-1 hidden md:flex'>/ </p>
                  <p> {moneyBack}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className='basis-1/4 text-right'>
          <LinkButton
            data-tracker-context={trackId}
            href={url}
            variant={buttonVariant || 'filled'}
            color='primary'
            slim={false}
            fullwidth
            openInNewTab={false}
            leftSection={
              texts.pricing_table.buy_now === buttonText ? (
                <SvgIcon src={iconShoppingCartSrc} className='h-5 w-5' />
              ) : (
                <SvgIcon src={iconRocketSrc} className='h-5 w-5' />
              )
            }
            classNames={{
              root: 'group w-full md:max-w-[160px] max-h-8',
              inner: 'flex items-center justify-center gap-4',
              rightSection:
                'group-hover:translate-x-2 duration-300 ease-in-out transition',
            }}
          >
            {buttonText}
          </LinkButton>
        </div>
      </div>
    </div>
  );
}
